<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <!-- Stepper Component -->
        <StepperComponent :steps="steps" :current-step="currentStep" :completed-steps="completedSteps"
          @step-change="handleStepChange" />

        <!-- Dynamic Step Content -->
        <div class="step-content-wrapper">
          <transition name="fade" mode="out-in">
            <component :is="getCurrentComponent" :key="currentStep" :form-data="getStepData(currentStepInfo.dataKey)"
              @update-data="handleDataUpdate" @next-step="handleNextStep" @previous-step="handlePreviousStep"
              @step-completed="handleStepCompleted" />
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepperComponent from "./stepperComponent.vue";
import SubjectConfiguration from "./psySectionConfig.vue";
import TopicsManagement from "./competencyConfig.vue";
import TraitConfiguration from "./traitConfig.vue";
import CategoriesSetup from "./categoriesSetup.vue";
import QuestionRules from "./questionRules.vue";
import SystemPreview from "./systemPreview.vue";
import DescriptorSetup from "./descriptor.vue"
export default {
  name: "SubjectManagement",
  components: {
    StepperComponent,
    SubjectConfiguration,
    TopicsManagement,
    TraitConfiguration,
    CategoriesSetup,
    QuestionRules,
    DescriptorSetup,
    SystemPreview,
  },
  data() {
    return {
      currentStep: 1,
      completedSteps: [],
      steps: [
        {
          id: 1,
          title: "PsySection Configuration",
          component: SubjectConfiguration,
          icon: "bi-gear",
          dataKey: "subjectConfiguration",
          description: "Basic subject setup",
        },
        {
          id: 2,
          title: "Competency Configuration",
          component: "TopicsManagement",
          icon: "bi-list-ul",
          dataKey: "topicsManagement",
          description: "Manage assessment topics",
        },
        {
          id: 3,
          title: "Trait Configuration",
          component: "TraitConfiguration",
          icon: "bi-sliders",
          dataKey: "traitConfiguration",
          description: "Configure personality traits",
        },
        {
          id: 4,
          title: "Categories Setup",
          component: "CategoriesSetup",
          icon: "bi-tags",
          dataKey: "categoriesSetup",
          description: "Setup assessment categories",
        },
        {
          id: 5,
          title: "Question Rules",
          component: "QuestionRules",
          icon: "bi-question-circle",
          dataKey: "questionRules",
          description: "Define question rules",
        },
        {
          id: 6,
          title: "Descriptor Configuration",
          component: "DescriptorSetup",
          icon: "bi-question-circle",
          dataKey: "descriptorSetup",
          description: "Add descriptive text",
        },
        {
          id: 7,
          title: "System Preview",
          component: "SystemPreview",
          icon: "bi-eye",
          dataKey: "systemPreview",
          description: "Preview and finalize",
        },
      ],
      // Store data for each step
      stepData: {
        subjectConfiguration: {
          subjectName: "",
          numQuestions: null,
          testTime: "",
          reportType: "",
          randomizeQuestions: false,
          allowReview: true,
        },
        topicsManagement: {
          topics: [],
          selectedTopics: [],
        },
        traitConfiguration: {
          traits: [],
          weights: {},
        },
        categoriesSetup: {
          categories: [],
          subcategories: {},
        },
        questionRules: {
          rules: [],
          scoring: {},
        },
        descriptorSetup: {
          descriptors: [],
          selectedDescriptors: [],
        },
        systemPreview: {
          isComplete: false,
          summary: {},
        },
      },
    };
  },
  computed: {
    getCurrentComponent() {
      const step = this.steps.find((s) => s.id === this.currentStep);
      return step ? step.component : "SubjectConfiguration";
    },
    currentStepInfo() {
      return this.steps.find((s) => s.id === this.currentStep) || this.steps[0];
    },
    totalSteps() {
      return this.steps.length;
    },
    progressPercentage() {
      return Math.round((this.currentStep / this.totalSteps) * 100);
    },
  },
  methods: {

    // ...mapActions([
    //   "GetTraitData",
    //   "GetCompetencyData",
    //   "GetCategoryData"]),

    // async getCompetencyData() {
    //   var response = await this.GetCompetencyData({
    //     pageNumber: 1,
    //     pageSize: 100,
    //     searchStr: "",
    //   });

    //   console.log("GetCompetencyData response:::", response)
    // },

    // async getTraitData() {
    //   var response = await this.GetTraitData({
    //     pageNumber: 1,
    //     pageSize: 100,
    //     searchStr: "",
    //   });

    //   console.log("GetTraitData response:::", response)
    // },

    // async getCategoryData() {
    //   var response = await this.GetCategoryData({
    //     pageNumber: 1,
    //     pageSize: 100,
    //     searchStr: "",
    //   });

    //   console.log("GetCategoryData response:::", response)
    // },

    handleStepChange(stepId) {
      // Validate if step can be changed
      if (this.canChangeToStep(stepId)) {
        this.currentStep = stepId;
        this.saveCurrentState();
      } else {
        this.showNotification(
          "Please complete the current step before proceeding",
          "warning"
        );
      }
    },

    canChangeToStep(stepId) {
      // Allow going back to completed steps or current step
      if (stepId <= this.currentStep || this.completedSteps.includes(stepId)) {
        return true;
      }

      // Allow going forward only if current step is completed
      if (
        stepId === this.currentStep + 1 &&
        this.completedSteps.includes(this.currentStep)
      ) {
        return true;
      }

      return false;
    },

    handleNextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep += 1;
        this.saveCurrentState();
      }
    },

    handlePreviousStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
        this.saveCurrentState();
      }
    },

    handleStepCompleted(stepId) {
      if (!this.completedSteps.includes(stepId)) {
        this.completedSteps.push(stepId);
      }
      this.saveCurrentState();

      // Auto advance to next step if not on last step
      if (stepId < this.totalSteps) {
        setTimeout(() => {
          this.handleNextStep();
        }, 500);
      }
    },

    handleDataUpdate({ stepKey, data }) {
      // Update step data
      if (this.stepData[stepKey]) {
        this.stepData[stepKey] = { ...this.stepData[stepKey], ...data };
      }
      this.saveCurrentState();
    },

    getStepData(dataKey) {
      return this.stepData[dataKey] || {};
    },

    getAllStepData() {
      return { ...this.stepData };
    },

    saveCurrentState() {
      const stateToSave = {
        currentStep: this.currentStep,
        completedSteps: this.completedSteps,
        stepData: this.stepData,
        timestamp: new Date().toISOString(),
      };

      try {
        localStorage.setItem(
          "subjectManagementState",
          JSON.stringify(stateToSave)
        );
      } catch (error) {
        console.error("Failed to save state:", error);
      }
    },

    loadSavedState() {
      try {
        const savedState = localStorage.getItem("subjectManagementState");
        if (savedState) {
          const state = JSON.parse(savedState);
          this.currentStep = state.currentStep || 1;
          this.completedSteps = state.completedSteps || [];
          this.stepData = { ...this.stepData, ...state.stepData };
        }
      } catch (error) {
        console.error("Failed to load saved state:", error);
      }
    },

    resetAllData() {
      if (
        confirm(
          "Are you sure you want to reset all data? This action cannot be undone."
        )
      ) {
        this.currentStep = 1;
        this.completedSteps = [];

        // Reset all step data to initial values
        Object.keys(this.stepData).forEach((key) => {
          if (key === "subjectConfiguration") {
            this.stepData[key] = {
              subjectName: "",
              numQuestions: null,
              testTime: "",
              reportType: "",
              randomizeQuestions: false,
              allowReview: true,
            };
          } else if (Array.isArray(this.stepData[key])) {
            this.stepData[key] = [];
          } else if (typeof this.stepData[key] === "object") {
            this.stepData[key] = {};
          }
        });

        // Clear localStorage
        localStorage.removeItem("subjectManagementState");

        this.showNotification("All data has been reset", "info");
      }
    },

    // exportData() {
    //   const exportData = {
    //     metadata: {
    //       exportDate: new Date().toISOString(),
    //       version: "1.0",
    //       totalSteps: this.totalSteps,
    //       completedSteps: this.completedSteps.length,
    //     },
    //     configuration: this.getAllStepData(),
    //   };

    //   const dataStr = JSON.stringify(exportData, null, 2);
    //   const dataBlob = new Blob([dataStr], { type: "application/json" });
    //   const url = URL.createObjectURL(dataBlob);

    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.download = `subject-management-config-${new Date().toISOString().split("T")[0]
    //     }.json`;
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    //   URL.revokeObjectURL(url);

    //   this.showNotification("Configuration exported successfully", "success");
    // },

    showNotification(message, type = "info", duration = 5000) {
      // Create notification
      const notification = document.createElement("div");
      notification.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
      notification.style.cssText = `
        top: 20px; 
        right: 20px; 
        z-index: 9999; 
        min-width: 300px;
        max-width: 400px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        border-radius: 8px;
      `;

      const icons = {
        success: "bi-check-circle-fill",
        error: "bi-x-circle-fill",
        info: "bi-info-circle-fill",
        warning: "bi-exclamation-triangle-fill",
      };

      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="${icons[type]} me-2"></i>
          <div>${message}</div>
          <button type="button" class="btn-close ms-auto" data-bs-dismiss="alert"></button>
        </div>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.classList.remove("show");
          setTimeout(() => {
            if (notification.parentNode) {
              notification.parentNode.removeChild(notification);
            }
          }, 150);
        }
      }, duration);
    },
  },

  mounted() {
    // Load saved state on mount
    this.loadSavedState();

    // this.getCompetencyData();
    // this.getTraitData();
    // this.getCategoryData();

    // Add keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "s") {
          e.preventDefault();
          this.saveCurrentState();
          this.showNotification("State saved", "info");
        } else if (e.key === "r") {
          e.preventDefault();
          this.resetAllData();
        }
      }

      // Arrow key navigation
      if (e.key === "ArrowLeft" && e.altKey) {
        e.preventDefault();
        this.handlePreviousStep();
      } else if (e.key === "ArrowRight" && e.altKey) {
        e.preventDefault();
        this.handleNextStep();
      }
    });
  },

  beforeUnmount() {
    // Save state before component unmount
    this.saveCurrentState();
  },
};
</script>

<style scoped>
.page-header {
  text-align: center;
  padding: 2rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.1rem;
  margin-bottom: 0;
}

.step-content-wrapper {
  min-height: 600px;
  position: relative;
}

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Responsive design */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }

  .page-header {
    padding: 1.5rem 0;
  }

  .step-content-wrapper {
    min-height: 500px;
  }
}

@media (max-width: 576px) {
  .page-title {
    font-size: 1.75rem;
  }

  .page-header {
    padding: 1rem 0;
  }
}
</style>
